import { useCallback, useEffect } from 'react';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';

import { Skeleton } from '@shared/ui/skeleton';
import { useIsOnline } from '@shared/lib/hooks';
import { reportClientError } from '@shared/observability';

/**
 * Route-level pending + error boundaries
 * (improvements/08-routing-tanstack-router.md, step 4). Wired once as
 * `defaultPendingComponent`/`defaultErrorComponent` in `createTenantApp`, so
 * every route in every template inherits them instead of falling through to
 * the app-wide full-screen spinner chain the topic file calls out.
 *
 * Deliberately layout-shaped rather than a spinner: a skeleton that echoes
 * "topbar + content" reads as the page arriving, which is the native-feel
 * goal, and it can't spin forever if a chunk never resolves -- the router
 * swaps it for `RouteError` in that case.
 */
export function RoutePending() {
	return (
		<div
			data-testid="route-pending"
			className="flex h-dvh w-full flex-col gap-4 p-4"
			aria-busy="true"
			aria-live="polite"
		>
			<Skeleton className="h-12 w-full" />
			<Skeleton className="h-48 w-full" />
			<div className="grid grid-cols-2 gap-4">
				<Skeleton className="h-32 w-full" />
				<Skeleton className="h-32 w-full" />
			</div>
		</div>
	);
}

/**
 * `reset` alone is not enough here: it only resets the React error boundary,
 * so a route whose loader failed re-renders and fails again with the same
 * cached error. `router.invalidate()` re-runs the loaders first, which is what
 * "Retry" has to mean for a data-loading failure -- the pattern TanStack's own
 * `ErrorComponent` docs use.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
	const router = useRouter();
	const isOnline = useIsOnline();

	const onRetry = useCallback(() => {
		reset();
		void router.invalidate();
	}, [reset, router]);

	// Side effect, not inline in render -- this component re-renders on
	// every retry attempt with the same cached error until a real
	// navigation clears it, and reporting must reflect that it happened
	// once, not once per render. `category` is the route path: the local
	// reporter dedupes on `name + category`, and without a per-route
	// category every route failure in the app would collapse into one row.
	const pathname = router.state.location.pathname;
	useEffect(() => {
		reportClientError({ source: 'router', category: pathname, error });
	}, [error, pathname]);

	return (
		<div
			data-testid="route-error"
			className="flex h-dvh w-full flex-col items-center justify-center gap-4 p-6 text-center"
			role="alert"
		>
			<p className="text-lg font-semibold">
				{isOnline
					? 'Something went wrong loading this page.'
					: "You're offline."}
			</p>
			<p className="max-w-prose text-sm opacity-70">
				{isOnline
					? (error instanceof Error && error.message) ||
						'Please try again.'
					: 'This page has not been downloaded for offline reading. Reconnect and try again.'}
			</p>
			<div className="flex gap-3">
				<button
					type="button"
					onClick={onRetry}
					className="rounded-md border px-4 py-2 text-sm"
				>
					Try again
				</button>
				<button
					type="button"
					onClick={() => void router.navigate({ to: '/' })}
					className="rounded-md px-4 py-2 text-sm underline"
				>
					Go home
				</button>
			</div>
		</div>
	);
}
